import { solveCramer4 } from './solvers.ts';

const G = 9.81;

export interface ElasticState {
  theta1: number; omega1: number; r1: number; rDot1: number;
  theta2: number; omega2: number; r2: number; rDot2: number;
}

export function elasticDerivatives(
  sa: number[], sb: number[],
  m1: number, m2: number, L1: number, L2: number, k1: number, k2: number,
): { da: number[]; db: number[] } {
  const M1 = m1, M2 = m2, TOTAL_M = M1 + M2;
  const L10 = L1, L20 = L2;
  const l1 = L10 + sa[2];
  const l2 = L20 + sb[2];
  const delta = sa[0] - sb[0];
  const sinD = Math.sin(delta);
  const cosD = Math.cos(delta);
  const l1sq = l1 * l1;
  const l2sq = l2 * l2;
  const w1sq = sa[1] * sa[1];
  const w2sq = sb[1] * sb[1];

  const M: number[][] = [
    [TOTAL_M * l1sq, 0,              M2 * l1 * l2 * cosD, -M2 * l1 * sinD],
    [0,              TOTAL_M,         M2 * l2 * sinD,      M2 * cosD],
    [M2 * l1 * l2 * cosD, M2 * l2 * sinD, M2 * l2sq,     0],
    [-M2 * l1 * sinD, M2 * cosD,     0,                    M2],
  ];

  const f: number[] = [
    -2 * TOTAL_M * l1 * sa[3] * sa[1]
    - 2 * M2 * l1 * sb[3] * sb[1] * cosD
    - M2 * l1 * l2 * w2sq * sinD
    - TOTAL_M * G * l1 * Math.sin(sa[0]),
    TOTAL_M * l1 * w1sq
    + M2 * l2 * w2sq * cosD
    - 2 * M2 * sb[3] * sb[1] * sinD
    + TOTAL_M * G * Math.cos(sa[0])
    - k1 * sa[2],
    -2 * M2 * l2 * sa[3] * sa[1] * cosD
    - 2 * M2 * l2 * sb[3] * sb[1]
    + M2 * l1 * l2 * w1sq * sinD
    - M2 * G * l2 * Math.sin(sb[0]),
    2 * M2 * sa[3] * sa[1] * sinD
    + M2 * l1 * w1sq * cosD
    + M2 * l2 * w2sq
    + M2 * G * Math.cos(sb[0])
    - k2 * sb[2],
  ];

  const accel = solveCramer4(M, f);
  return {
    da: [sa[1], accel[0], sa[3], accel[1]],
    db: [sb[1], accel[2], sb[3], accel[3]],
  };
}
